import { type FC, useMemo, useState } from "react";
import { Loader2 } from "lucide-react";

import { PeopleSelector } from "@/shared/components/PeopleSelector";
import { FieldError } from "@/shared/components/ui/FieldError";
import { OptionCard } from "@/shared/components/ui/OptionCard";
import { RadioGroup } from "@/shared/components/ui/radio-group";
import { EstateRole } from "@/shared/constants/contactDetailOptions";
import { useAppContext } from "@/shared/hooks/useAppContext";
import { useAssignLegalRole } from "@/shared/hooks/useAssignLegalRole";
import { useUserContacts } from "@/shared/hooks/useUserContacts";
import { saveConfirmedWill } from "@/shared/utils/confirmedEstateRecords";
import {
  formatContactName,
  isExecutorContact,
  isNominatedExecutor,
  isSuccessorExecutor,
} from "@/shared/utils/contactUtils";
import { sentryUtils } from "@/shared/utils/sentry";

import type { FlowCustomComponentProps } from "@/shared/constants/flowTypes";
import type { PersonContact } from "@/shared/types/estateContact";

const EXECUTOR_KEY = "executor_status";
const SUCCESSOR_KEY = "successor_executor";

export const ExecutorStatusScreen: FC<FlowCustomComponentProps> = ({
  answers,
  setAnswer,
  errors,
}) => {
  const { person } = useAppContext();
  const { contacts, isLoading } = useUserContacts();
  const assignLegalRole = useAssignLegalRole();
  const [isSaving, setIsSaving] = useState(false);

  // The will record feeds the Summary page, so it follows the same pick
  const reassign =
    (role: string, hasRole: (contact: PersonContact) => boolean, willKey: string) =>
    async (contactId: number | null) => {
      if (contactId !== null) {
        setIsSaving(true);
        try {
          await assignLegalRole(role, hasRole, [contactId]);
          if (person?.estate_id) {
            await saveConfirmedWill(person.estate_id, {}, undefined, { [willKey]: contactId });
          }
        } catch (err) {
          sentryUtils.captureError(err, "Saving the will executor failed", {
            estateId: person?.estate_id,
          });
        } finally {
          setIsSaving(false);
        }
      }
    };

  const executor = useMemo(
    () => contacts.find(isNominatedExecutor) ?? contacts.find(isExecutorContact),
    [contacts],
  );

  const successorExec = useMemo(() => contacts.find(isSuccessorExecutor), [contacts]);

  const executorName = executor ? formatContactName(executor.first_name, executor.last_name) : null;
  const successorName = successorExec
    ? formatContactName(successorExec.first_name, successorExec.last_name)
    : null;

  const executorStatus = answers[EXECUTOR_KEY] as string | undefined;
  const successorStatus = answers[SUCCESSOR_KEY] as string | undefined;

  const handleExecutorSelect = (value: string) => {
    setAnswer(EXECUTOR_KEY, value);
    if (value === "able_and_willing") {
      setAnswer(SUCCESSOR_KEY, "");
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="size-8 animate-spin text-brand" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-1">
        <p className="font-sans text-xl font-semibold leading-[30px]">
          Is {executorName ?? "the primary executor"} willing and able to serve?
        </p>
        <p className="text-sm text-secondary tracking-[-0.28px]">
          Confirm whether the primary executor named in the will can carry out their duties, or if
          an alternate executor needs to be appointed.
        </p>
      </div>

      <PeopleSelector
        mode="single"
        label="Executor named in the will"
        placeholder="Choose the executor"
        value={executor?.id ?? null}
        disabled={isSaving}
        onChange={reassign(EstateRole.NominatedExecutor, isNominatedExecutor, "namedExecutor")}
      />

      <RadioGroup
        name="executor_status"
        value={executorStatus ?? ""}
        onValueChange={handleExecutorSelect}
        className="flex flex-col gap-3"
      >
        <OptionCard
          label="Yes, executor able and willing"
          selected={executorStatus === "able_and_willing"}
          onClick={() => handleExecutorSelect("able_and_willing")}
          value="able_and_willing"
        />
        <OptionCard
          label="No, they are deceased, unwilling, or unable"
          selected={executorStatus === "unable"}
          onClick={() => handleExecutorSelect("unable")}
          value="unable"
        />
      </RadioGroup>
      <FieldError message={errors?.executor_status} />

      {executorStatus === "unable" && (
        <>
          <div className="border-t border-primary" />

          <div className="flex flex-col gap-1">
            <p className="font-sans text-xl font-semibold leading-[30px]">
              {successorName
                ? `We found ${successorName} listed as the alternate executor. Are they willing and able to serve?`
                : "Is there a successor executor named in the will who is willing to serve?"}
            </p>
            <p className="text-sm text-secondary tracking-[-0.28px]">
              {successorName
                ? `The will names ${successorName} to step in if ${executorName ?? "the primary executor"} is unavailable.`
                : "If the primary executor can't serve, the will may name a backup. Check the will for alternate or successor executor language."}
            </p>
          </div>

          <PeopleSelector
            mode="single"
            label="Successor executor"
            placeholder="Choose the successor executor"
            value={successorExec?.id ?? null}
            disabled={isSaving}
            onChange={reassign(
              EstateRole.SuccessorExecutor,
              isSuccessorExecutor,
              "successorExecutor",
            )}
            filter={(contact) => contact.id !== executor?.id}
          />

          <RadioGroup
            name="successor_executor"
            value={successorStatus ?? ""}
            onValueChange={(v) => setAnswer(SUCCESSOR_KEY, v)}
            className="flex flex-col gap-3"
          >
            <OptionCard
              label={
                successorName
                  ? `Yes, ${successorName} is able and willing`
                  : "Yes, successor executor is willing to serve"
              }
              selected={successorStatus === "successor_willing"}
              onClick={() => setAnswer(SUCCESSOR_KEY, "successor_willing")}
              value="successor_willing"
            />
            <OptionCard
              label="No, they are also unavailable or declining"
              selected={successorStatus === "all_decline"}
              onClick={() => setAnswer(SUCCESSOR_KEY, "all_decline")}
              value="all_decline"
            />
          </RadioGroup>
          <FieldError message={errors?.successor_executor} />
        </>
      )}
    </div>
  );
};
